import type { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import { MinusCircled, Person, PlusCircled } from '@storefront/lib-global/icons';
import {
  Container,
  ControlsWrapper,
  DecreaseButton,
  IconWrapper,
  IncreaseButton,
  Label,
  Value,
} from './NumberOfChildren.styles';

export enum NumberOfChildrenLabelVariant {
  DEFAULT = 'default',
  SHORT = 'short',
  QUANTITY = 'quantity',
}

export type NumberOfChildrenLabelType =
  | NumberOfChildrenLabelVariant.DEFAULT
  | NumberOfChildrenLabelVariant.SHORT
  | NumberOfChildrenLabelVariant.QUANTITY;

export const MIN_NUMBER_OF_CHILDREN = 1;
export const MAX_NUMBER_OF_CHILDREN = 6;

export type NumberOfChildrenProps = {
  value: number;
  labelType?: NumberOfChildrenLabelType;
  min?: number;
  max?: number;
  isRequired?: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
};

export function NumberOfChildren({
  value,
  labelType = NumberOfChildrenLabelVariant.DEFAULT,
  min = MIN_NUMBER_OF_CHILDREN,
  max = MAX_NUMBER_OF_CHILDREN,
  isRequired = false,
  onDecrease,
  onIncrease,
}: NumberOfChildrenProps): ReactElement {
  const { t } = useTranslation(['lib-appointments-common']);

  let labelText = t('appointment.number.of.children');
  if (labelType === NumberOfChildrenLabelVariant.SHORT) {
    labelText = t('appointment.number.of.children.short');
  } else if (labelType === NumberOfChildrenLabelVariant.QUANTITY) {
    labelText = t('appointment.number.of.children.quantity', { count: value });
  }

  if (isRequired) labelText += '*';

  return (
    <Container>
      <IconWrapper aria-hidden>
        <Person />
      </IconWrapper>
      <Label>{labelText}</Label>
      <ControlsWrapper>
        <Value>{value}</Value>
        <DecreaseButton
          type="button"
          aria-label={t('appointment.number.of.children.aria.decrease')}
          onClick={onDecrease}
          disabled={!!min && value <= min}
        >
          <MinusCircled />
        </DecreaseButton>
        <IncreaseButton
          type="button"
          aria-label={t('appointment.number.of.children.aria.increase')}
          onClick={onIncrease}
          disabled={!!max && value >= max}
        >
          <PlusCircled />
        </IncreaseButton>
      </ControlsWrapper>
    </Container>
  );
}
